import React, {
  createContext,
  useContext,
  useReducer,
  Dispatch,
  useEffect,
} from "react"
import useMeasure from "react-use-measure"

/** Type return values */
type HeightState = {
  header: number
  sidebarHeader: number
}
type HeightContextValue = [HeightState, Dispatch<Partial<HeightState>>]

/** Generate the context */
const HeightContext = createContext<HeightContextValue | undefined>(undefined)

/** Provider to wrap your app with */
export const HeightProvider: React.FC = ({ children }) => {
  /** Store heights in a reducer */
  const [heights, dispatch] = useReducer(
    (state: HeightState, action: Partial<HeightState>) => ({
      ...state,
      ...action,
    }),
    /** Initialise known heights we'll want */
    { header: 0, sidebarHeader: 0 }
  )

  return (
    <HeightContext.Provider value={[heights, dispatch]}>
      {children}
    </HeightContext.Provider>
  )
}

/** Hook to consume the context */
export const useHeights = () => {
  /** Grab the context */
  const context = useContext(HeightContext)

  /** Throw error if context is undefined */
  if (!context) throw Error("Not inside <HeightProvider />")

  /** Return the ref so the user can hook into the heights, as well as the state */
  return context
}

/** Returns a ref that will track the element's height */
export const useSetHeight = (
  key: string
): ((node: HTMLElement | null) => void) => {
  /** Track heights and dispatch when it changes */
  const [, dispatch] = useHeights()
  const [ref, { height }] = useMeasure()
  useEffect(() => {
    key && dispatch({ [key]: height })
  }, [height, key, dispatch])

  return ref
}
